import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Globe as GlobeIcon, Compass, GraduationCap, Users, Award, Briefcase, Building2, MessageCircle, MapPin, Mic, Star, Plane, TrendingUp, Mountain, Waves, TreePine } from "lucide-react";
import Hero3DGlobe from "@/components/Hero3DGlobe";
import HighlightBar from "@/components/HighlightBar";
import DestinationCard from "@/components/DestinationCard";
import StudentJourneyStrip from "@/components/StudentJourneyStrip";
import MountainSilhouette from "@/components/MountainSilhouette";
import MapleLeaf from "@/components/MapleLeaf";
import { BENEFITS, TESTIMONIALS } from "@/data/staticContent";
import api from "@/lib/api";

const iconMap = {
  Globe: GlobeIcon, Sparkles, MessageCircle, Briefcase, Users, Award, Compass, GraduationCap, Building2, Mic, MapPin, Star,
};

const BENEFIT_TONES = [
  { bg: "bg-[#FFE9E6]", ring: "ring-red-100", icon: "bg-[#D52B1E]" },
  { bg: "bg-[#FAF6EE]", ring: "ring-amber-100", icon: "bg-amber-600" },
  { bg: "bg-[#E4F1EE]", ring: "ring-emerald-100", icon: "bg-[#1F4A3A]" },
  { bg: "bg-[#E1F1F3]", ring: "ring-teal-100", icon: "bg-[#0E7C86]" },
  { bg: "bg-[#F3EADB]", ring: "ring-amber-100", icon: "bg-amber-700" },
  { bg: "bg-[#FFE9E6]", ring: "ring-red-100", icon: "bg-[#D52B1E]" },
  { bg: "bg-[#E4F1EE]", ring: "ring-emerald-100", icon: "bg-[#1F4A3A]" },
  { bg: "bg-[#E1F1F3]", ring: "ring-teal-100", icon: "bg-[#0E7C86]" },
];

const STATS = [
  { value: "30+", label: "Programs", tone: "from-[#D52B1E] to-[#EF4E33]" },
  { value: "4", label: "Active countries", tone: "from-[#1F4A3A] to-emerald-500" },
  { value: "15", label: "Coming soon", tone: "from-amber-500 to-amber-300" },
  { value: "10", label: "Journey steps", tone: "from-[#0E7C86] to-teal-400" },
];

const TESTIMONIAL_ACCENTS = {
  red: { bg: "bg-[#FFF5F3]", border: "border-red-100", avatar: "bg-[#D52B1E] text-white", chip: "text-[#D52B1E]" },
  amber: { bg: "bg-[#FAF6EE]", border: "border-amber-100", avatar: "bg-amber-600 text-white", chip: "text-amber-700" },
  teal: { bg: "bg-[#E1F1F3]", border: "border-teal-100", avatar: "bg-[#0E7C86] text-white", chip: "text-[#0E7C86]" },
  emerald: { bg: "bg-[#E4F1EE]", border: "border-emerald-100", avatar: "bg-[#1F4A3A] text-white", chip: "text-[#1F4A3A]" },
};

export default function Home() {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/destinations").then(({ data }) => {
      setDestinations(data);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — Canadian mountains & lake backdrop */}
      <section className="relative overflow-hidden bg-[#FAF6EE]">
        {/* Mountain / lake photographic backdrop */}
        <div className="canada-hero-bg" />

        {/* Ambient color blobs (warmer palette) */}
        <div className="blob bg-red-300 w-[440px] h-[440px] -top-32 -right-24" />
        <div className="blob bg-amber-200 w-[380px] h-[380px] top-40 -left-24" />
        <div className="blob bg-emerald-200 w-[300px] h-[300px] bottom-16 right-1/4 opacity-40" />

        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 lg:pt-20 pb-14 grid lg:grid-cols-2 gap-10 items-center relative">          <div>
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border border-red-100 text-xs font-semibold text-slate-800 shadow-md mb-6"
            >
              <MapleLeaf className="w-3.5 h-3.5 text-[#D52B1E]" />
              Canadian-inspired programs across the world
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.02] tracking-tight"
            >
              Learn <span className="gradient-maple-text">Beyond</span><br />
              <span className="relative inline-block">
                Borders
                <span className="absolute -bottom-2 left-0 right-0 h-3 bg-[#D52B1E]/25 -z-10 rounded-full" />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-6 text-lg text-slate-700 leading-relaxed max-w-xl"
            >
              Short-term international learning, cultural experiences, industry exposure, workshops, and global connections for students from around the world.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15 }}
              className="mt-4 text-sm text-slate-600 max-w-xl italic"
            >
              Travel. Learn new skills. Meet new people. Experience new cultures. Build a global perspective.
            </motion.p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/programs"
                data-testid="hero-cta-explore-programs"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full gradient-maple text-white font-semibold text-sm shadow-xl shadow-red-500/30 hover:shadow-2xl hover:-translate-y-0.5 transition-transform"
              >
                Explore Programs <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/destinations"
                data-testid="hero-cta-view-destinations"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 hover:border-slate-300 text-slate-900 font-semibold text-sm shadow-sm transition-colors"
              >
                View Destinations
              </Link>
            </div>

            {/* Stats strip */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl" data-testid="hero-stats">
              {STATS.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.05 }}
                  className="relative overflow-hidden rounded-2xl bg-white/95 backdrop-blur border border-slate-100 shadow-md p-3"
                >
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${s.tone}`} />
                  <p className="text-2xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>{s.value}</p>
                  <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="w-[85%] aspect-square rounded-full bg-gradient-to-br from-red-300/35 via-amber-200/25 to-emerald-300/25 blur-2xl" />
            </div>
            <Hero3DGlobe />
            <div className="hidden md:flex float-slow absolute -top-4 -left-4 items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-red-100 shadow-lg text-xs font-semibold text-[#D52B1E]">
              <Plane className="w-3.5 h-3.5 -rotate-45" /> Malaysia → Canada
            </div>
            <div className="hidden md:flex float-med absolute top-1/4 -right-4 items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-emerald-100 shadow-lg text-xs font-semibold text-[#1F4A3A]">
              <GraduationCap className="w-3.5 h-3.5" /> India → Canada
            </div>
            <div className="hidden md:flex float-slow absolute bottom-8 -left-6 items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-amber-100 shadow-lg text-xs font-semibold text-amber-800">
              <GlobeIcon className="w-3.5 h-3.5" /> SE Asia → Indonesia
            </div>
          </div>
        </div>

        {/* Mountain silhouette footer transitioning into forest band */}
        <div className="relative -mb-1">
          <MountainSilhouette className="w-full h-32 md:h-40 block relative z-10" />
        </div>
      </section>

      {/* Highlight bar sits on forest-green band */}
      <section className="bg-[#1F4A3A] py-10">
        <HighlightBar />
      </section>

      {/* WHY */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 relative">
          <div className="max-w-3xl mb-12">
            <p className="inline-flex items-center gap-2 text-sm font-bold text-[#D52B1E] uppercase tracking-widest mb-3">
              <MapleLeaf className="w-4 h-4" /> Why StudyTrip Global
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight">
              More Than a Course. <br />
              <span className="gradient-maple-text">A Global Experience.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              StudyTrip Global gives students an opportunity to learn outside the classroom, experience new cultures, visit international organizations, attend practical workshops, build confidence, meet students from different countries, and develop a broader understanding of the world.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {BENEFITS.map((b, i) => {
              const Icon = iconMap[b.icon] || Sparkles;
              const tone = BENEFIT_TONES[i % BENEFIT_TONES.length];
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                  className={`p-5 rounded-2xl ${tone.bg} ring-1 ${tone.ring} hover:shadow-xl hover:shadow-slate-200 hover:-translate-y-1 transition-transform`}
                  data-testid={`benefit-${i}`}
                >
                  <div className={`w-10 h-10 rounded-xl ${tone.icon} text-white grid place-items-center mb-3 shadow-md`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-semibold text-slate-900 leading-snug">{b.title}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DESTINATIONS on cream background with lake band */}
      <section className="relative overflow-hidden bg-[#FAF6EE]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <p className="inline-flex items-center gap-2 text-sm font-bold text-[#0E7C86] uppercase tracking-widest mb-3">
                <Waves className="w-4 h-4" /> Destinations
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight">
                Choose Your{" "}
                <span className="gradient-maple-text">Learning Destination</span>
              </h2>
            </div>
            <Link
              to="/destinations"
              data-testid="home-see-all-destinations"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-[#D52B1E]"
            >
              See all destinations <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <p className="text-slate-500">Loading destinations…</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {destinations.map((d, i) => (
                <DestinationCard key={d.id} dest={d} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* STUDENT JOURNEY - forest/lake band */}
      <section className="relative overflow-hidden py-24 bg-gradient-to-br from-[#E4F1EE] via-white to-[#E1F1F3]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <p className="inline-flex items-center gap-2 text-sm font-bold text-[#1F4A3A] uppercase tracking-widest mb-3">
            <TreePine className="w-4 h-4" /> Student Journey
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight mb-8">
            From your home country to a global classroom
          </h2>
          <StudentJourneyStrip />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="mb-10">
          <p className="inline-flex items-center gap-2 text-sm font-bold text-amber-700 uppercase tracking-widest mb-3">
            <Star className="w-4 h-4" /> Student Stories
          </p>
          <h2 className="font-display text-3xl font-extrabold text-slate-900 mb-2">
            Voices from around the world
          </h2>
          <p className="text-slate-500 text-sm">
            A glimpse of the kinds of experiences students take home from a StudyTrip Global program.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TESTIMONIALS.map((t) => {
            const tone = TESTIMONIAL_ACCENTS[t.accent] || TESTIMONIAL_ACCENTS.red;
            return (
              <div
                key={t.id}
                className={`p-6 rounded-3xl border shadow-md ${tone.bg} ${tone.border} flex flex-col`}
                data-testid={`testimonial-${t.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-11 h-11 rounded-2xl grid place-items-center font-display font-extrabold text-sm ${tone.avatar}`}>
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 leading-tight">{t.name}</p>
                    <p className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {t.country}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed italic mb-4 flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="pt-3 mt-auto border-t border-slate-200/60 flex items-center justify-between text-[11px]">
                  <span className={`font-bold ${tone.chip}`}>{t.route}</span>
                  <span className="text-slate-500">{t.program}</span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-xs text-slate-400 italic">
          *Representative sample of the kinds of experiences students describe. Verified individual testimonials will be published as they are collected.
        </p>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="relative rounded-[36px] overflow-hidden gradient-maple text-white p-10 md:p-16">
          <div className="absolute -top-10 -right-10 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-16 -left-16 w-96 h-96 bg-amber-300/25 rounded-full blur-3xl" />
          <MapleLeaf className="absolute top-8 right-8 w-16 h-16 text-white/10" />
          <div className="relative max-w-2xl">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-4 text-white/80">
              <Mountain className="w-3.5 h-3.5" /> Your global classroom
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
              Your Classroom Can Be Anywhere
            </h2>
            <p className="mt-5 text-white/90 text-lg leading-relaxed">
              Discover new cultures, learn practical skills, meet international students, visit inspiring organizations, and build a global perspective.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/apply"
                data-testid="home-final-cta-apply"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm hover:-translate-y-0.5 transition-transform shadow-lg shadow-orange-500/40"
              >
                Apply Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/programs"
                data-testid="home-final-cta-find-program"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold text-sm transition-colors"
              >
                Find Your Program
              </Link>
              <Link
                to="/partner"
                data-testid="home-final-cta-institution"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold text-sm transition-colors"
              >
                Travel With Your Institution
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
